/**
 * Catalog Cache Module
 *
 * Fetches real-time availability AND tags for ALL products in a single API
 * call, caches the result in localStorage for 60 minutes.
 *
 * Usage:
 *   const stock = await getStock();
 *   const variants = stock["vanyx-karabiner-12kn"];
 *   // variants = [{ id, available, quantity }, ...]
 *
 *   const tags = await getProductTags();
 *   const t = tags["vanyx-karabiner-12kn"]; // ["Merchandising", "label__30%:primary", ...]
 */

const CACHE_KEY = "vanyx_catalog";
const CACHE_TTL = 60 * 60 * 1000; // 60 minutes
const API_VERSION = "2024-10";

function getConfig() {
  const cfg = window.VANYX_CONFIG || {};
  return {
    domain: cfg.shopifyDomain || "shop.vanyx.io",
    token: cfg.shopifyToken || "",
  };
}

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) {
      return null;
    }
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) {
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // localStorage full or unavailable
  }
}

async function fetchCatalog() {
  const { domain, token } = getConfig();
  if (!token) {
    return null;
  }

  const lang = (window.VANYX_CONFIG || {}).lang || "de";
  const stock = {};
  const tags = {};
  let cursor = null;
  let hasNext = true;

  const query = `query ($cursor: String) {
    products(first: 50, after: $cursor) {
      pageInfo { hasNextPage endCursor }
      nodes {
        handle
        tags
        variants(first: 50) {
          nodes {
            id
            availableForSale
            quantityAvailable
          }
        }
      }
    }
  }`;

  while (hasNext) {
    const res = await fetch(
      `https://${domain}/api/${API_VERSION}/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
          "Accept-Language": lang,
        },
        body: JSON.stringify({ query, variables: { cursor } }),
      },
    );

    if (!res.ok) {
      return null;
    }

    const json = await res.json();
    if (json.errors) {
      return null;
    }

    const { nodes, pageInfo } = json.data.products;
    for (const product of nodes) {
      stock[product.handle] = product.variants.nodes.map((v) => ({
        id: v.id,
        available: v.availableForSale,
        // Shopify returns a negative net-across-locations total for oversold
        // variants that are still "continue selling". Treat negatives as
        // untracked (null) so the low-stock display never shows a bogus count.
        quantity:
          v.quantityAvailable !== null &&
          v.quantityAvailable !== undefined &&
          v.quantityAvailable < 0
            ? null
            : v.quantityAvailable,
      }));
      tags[product.handle] = product.tags || [];
    }

    hasNext = pageInfo.hasNextPage;
    cursor = pageInfo.endCursor;
  }

  return { stock, tags };
}

// Singleton promise to avoid duplicate fetches
let fetchPromise = null;

async function getCatalog() {
  const cached = readCache();
  if (cached) {
    return cached;
  }

  if (!fetchPromise) {
    fetchPromise = fetchCatalog().then((data) => {
      if (data) {
        writeCache(data);
      }
      fetchPromise = null;
      return data;
    });
  }

  return fetchPromise;
}

/**
 * Returns availability data for all products.
 * Uses localStorage cache if fresh (< 60 min), otherwise fetches from Shopify.
 * @returns {Promise<Object|null>} Map of handle → variant availability array
 */
export async function getStock() {
  const catalog = await getCatalog();
  return catalog ? catalog.stock : null;
}

/**
 * Returns the Shopify tag list for all products (same cached fetch as getStock).
 * @returns {Promise<Object|null>} Map of handle → array of tag strings
 */
export async function getProductTags() {
  const catalog = await getCatalog();
  return catalog ? catalog.tags : null;
}
